import { LINK_PASSWORD_CONTEXT, type LinkCode, formatLinkCode, linkCodePassword, randomLinkWords } from "./codes"
import { RendezvousError, type RendezvousTransport, awaitSlot } from "./rendezvous"
import { SPAKE2_ELEMENT_BYTES, Spake2, type Spake2Keys, concatBytes, open, passwordScalar, seal, sealingKey } from "./spake2"

/**
 * The code-word ceremony. Two WildLark clients that share a short code meet
 * on a nameplate, run SPAKE2, and swap introduction cards inside the derived
 * key. A card carries a person's name and glyph plus a one-use pairing invite
 * for that person on the sender's computer, minted with no access, so the
 * result of linking is two zero-access people who can then be granted access
 * from the People section.
 *
 * Slots: the initiator writes `a` (its element), the joiner writes `b` (its
 * element followed by its sealed card), and the initiator answers with `a2`
 * (its sealed card). A wrong code makes `b` undecryptable and burns the code.
 */

export interface LinkCard {
    v: 1
    name: string
    glyph?: string
    color?: string
    /** One-use pairing invite for the recipient on the sender's computer. */
    invite: string
    /** The principal the sender minted for the recipient, so it can be renamed later. */
    principalId?: string
    nodeId?: string
}

export interface LinkIdentity {
    name: string
    glyph?: string
    color?: string
}

export interface LinkInviteMinter {
    /** Mints a zero-access person invite for someone with this display name. */
    (personName: string): Promise<{ invite: string; principalId: string; nodeId: string }>
}

export interface LinkOptions {
    transport: RendezvousTransport
    identity: LinkIdentity
    mintInvite: LinkInviteMinter
    ttlSeconds?: number
    words?: number
    signal?: AbortSignal
    now?: () => number
}

export interface LinkOffer {
    code: LinkCode
    codeText: string
    expiresAt: string
    /** Resolves when the other side joined and both cards were exchanged. */
    completion: Promise<LinkCard>
    cancel(): Promise<void>
}

const INITIATOR_IDENTITY = new TextEncoder().encode("wildlark-link-initiator")
const JOINER_IDENTITY = new TextEncoder().encode("wildlark-link-joiner")
const JOINER_LABEL = "joiner-card"
const INITIATOR_LABEL = "initiator-card"
const MAX_CARD_BYTES = 6 * 1024
const DEFAULT_TTL_SECONDS = 30 * 60
const PLACEHOLDER_NAME = "Linked colleague"

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function encodeCard(card: LinkCard): Uint8Array {
    const bytes = new TextEncoder().encode(JSON.stringify(card))
    if (bytes.length > MAX_CARD_BYTES) throw new Error("The introduction is too large to send")
    return bytes
}

export function parseLinkCard(bytes: Uint8Array): LinkCard {
    let value: unknown
    try {
        value = JSON.parse(new TextDecoder().decode(bytes))
    } catch {
        throw new RendezvousError("invalid_card", "The other side sent an unreadable introduction")
    }
    if (!isRecord(value) || value.v !== 1 || typeof value.name !== "string" || !value.name.trim() || typeof value.invite !== "string" || !value.invite) {
        throw new RendezvousError("invalid_card", "The other side sent an invalid introduction")
    }
    return {
        v: 1,
        name: value.name.trim().slice(0, 80),
        invite: value.invite,
        ...(typeof value.glyph === "string" && value.glyph.trim() ? { glyph: value.glyph.trim().slice(0, 4) } : {}),
        ...(typeof value.color === "string" && value.color.trim() ? { color: value.color.trim().slice(0, 40) } : {}),
        ...(typeof value.principalId === "string" && value.principalId ? { principalId: value.principalId } : {}),
        ...(typeof value.nodeId === "string" && value.nodeId ? { nodeId: value.nodeId } : {}),
    }
}

async function keysFor(code: LinkCode, role: "a" | "b", element?: Uint8Array): Promise<{ spake: Spake2; w: Uint8Array }> {
    const w = await passwordScalar(LINK_PASSWORD_CONTEXT, `${code.nameplate} ${linkCodePassword(code)}`)
    return { spake: new Spake2(role, w, INITIATOR_IDENTITY, JOINER_IDENTITY, element), w }
}

async function openCard(keys: Spake2Keys, label: string, sealed: Uint8Array): Promise<LinkCard> {
    let plaintext: Uint8Array
    try {
        plaintext = await open(await sealingKey(keys, label), sealed)
    } catch {
        throw new RendezvousError("code_mismatch", "The code did not match. Ask for a fresh one and try again.")
    }
    return parseLinkCard(plaintext)
}

/** Creates a code and waits for a colleague to join with it. */
export async function offerLink(options: LinkOptions): Promise<LinkOffer> {
    const now = options.now ?? (() => Date.now())
    const ttlSeconds = options.ttlSeconds ?? DEFAULT_TTL_SECONDS
    const plate = await options.transport.allocate(ttlSeconds, options.signal)
    const code: LinkCode = { nameplate: plate.nameplate, words: randomLinkWords(options.words ?? (ttlSeconds > 60 * 60 ? 3 : 2)) }
    const { spake } = await keysFor(code, "a")
    await options.transport.put(plate.nameplate, "a", spake.publicElement, plate.holder, options.signal)
    const deadline = now() + ttlSeconds * 1000
    let cancelled = false
    const completion = (async (): Promise<LinkCard> => {
        const reply = await awaitSlot(options.transport, plate.nameplate, "b", plate.holder, { deadline, signal: options.signal, now })
        if (reply.length <= SPAKE2_ELEMENT_BYTES) throw new RendezvousError("invalid_card", "The other side sent an invalid response")
        const keys = await spake.finish(reply.slice(0, SPAKE2_ELEMENT_BYTES))
        const joiner = await openCard(keys, JOINER_LABEL, reply.slice(SPAKE2_ELEMENT_BYTES))
        const minted = await options.mintInvite(joiner.name)
        const card: LinkCard = {
            v: 1,
            name: options.identity.name,
            invite: minted.invite,
            principalId: minted.principalId,
            nodeId: minted.nodeId,
            ...(options.identity.glyph ? { glyph: options.identity.glyph } : {}),
            ...(options.identity.color ? { color: options.identity.color } : {}),
        }
        await options.transport.put(plate.nameplate, "a2", await seal(await sealingKey(keys, INITIATOR_LABEL), encodeCard(card)), plate.holder, options.signal)
        return joiner
    })()
    return {
        code,
        codeText: formatLinkCode(code),
        expiresAt: plate.expiresAt,
        completion,
        cancel: async () => {
            if (cancelled) return
            cancelled = true
            await options.transport.remove(plate.nameplate, plate.holder).catch(() => undefined)
        },
    }
}

/** Joins with a code someone else created and returns their card. */
export async function joinLink(code: LinkCode, options: LinkOptions): Promise<LinkCard> {
    const now = options.now ?? (() => Date.now())
    const deadline = now() + (options.ttlSeconds ?? DEFAULT_TTL_SECONDS) * 1000
    let offer: Uint8Array | null
    try {
        offer = await options.transport.get(code.nameplate, "a", null, false, options.signal)
    } catch (error) {
        if (error instanceof RendezvousError && error.code === "unknown_nameplate") {
            throw new RendezvousError("unknown_code", "That code is not active. Check the number or ask for a new one.")
        }
        throw error
    }
    if (!offer || offer.length !== SPAKE2_ELEMENT_BYTES)
        throw new RendezvousError("not_ready", "That code is not ready yet. Ask the other side to keep WildLark open.")
    const { spake } = await keysFor(code, "b")
    const keys = await spake.finish(offer)
    // The joiner mints its invite before it knows the initiator's name; the
    // caller renames the principal once the initiator's card arrives.
    const minted = await options.mintInvite(PLACEHOLDER_NAME)
    const card: LinkCard = {
        v: 1,
        name: options.identity.name,
        invite: minted.invite,
        principalId: minted.principalId,
        nodeId: minted.nodeId,
        ...(options.identity.glyph ? { glyph: options.identity.glyph } : {}),
        ...(options.identity.color ? { color: options.identity.color } : {}),
    }
    const sealedCard = await seal(await sealingKey(keys, JOINER_LABEL), encodeCard(card))
    try {
        await options.transport.put(code.nameplate, "b", concatBytes(spake.publicElement, sealedCard), null, options.signal)
    } catch (error) {
        if (error instanceof RendezvousError && error.code === "claimed") {
            throw new RendezvousError("claimed", "Someone already used this code. Ask for a fresh one.")
        }
        throw error
    }
    const reply = await awaitSlot(options.transport, code.nameplate, "a2", null, { deadline, signal: options.signal, now })
    return openCard(keys, INITIATOR_LABEL, reply)
}

export { PLACEHOLDER_NAME as LINK_PLACEHOLDER_NAME }
